<script setup lang="ts">
// Support action: discard a participant's questionnaire so it can be filled in again (RDT-39).
// Destructive — the stored answers are deleted — so it always asks first, and the confirmation spells
// out the two things support gets asked about: the existing link keeps working and no further profile
// is charged. The backend refuses statuses where a reset would be wrong (a participant that was never
// activated above all), which surfaces here as its own message rather than a generic failure.
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../api";
import { useConfirmedParticipantAction } from "../../composables/useConfirmedParticipantAction.ts";
import { Participant } from "../../types/PersologOnlineTypes.ts";

const props = defineProps({
    participant: {
        type: Object as PropType<Participant>,
        required: true,
    },
});

const emit = defineEmits<{ reset: [participant: Participant] }>();

const { t } = useI18n();

/** Nothing to reset before the questionnaire link went out; the backend refuses those too. */
const RESETTABLE_STATUSES = ["ACTIVATED", "STARTED", "COMPLETED", "CALCULATED", "FILLED", "GENERATED", "NOTIFIED"];

const disabled = computed(() => !props.participant.id || !RESETTABLE_STATUSES.includes(props.participant.status ?? ""));

const confirmGroup = computed(() => `reset-questionnaire-${props.participant.id}`);

const { loading, askForConfirmation } = useConfirmedParticipantAction({
    participant: () => props.participant,
    messages: "participants.reset",
    confirmGroup: () => confirmGroup.value,
    logMessage: "Could not reset the questionnaire",
    action: (participantId) => ProfileAdministrationAPI.resetQuestionnaire(participantId),
    onDone: (updated) => emit("reset", updated),
});
</script>

<template>
    <ConfirmDialog :group="confirmGroup" />
    <Button
        :data-cy="`reset-questionnaire-button-${participant.id}`"
        icon="pi pi-undo"
        rounded
        outlined
        severity="secondary"
        class="mr-2"
        :aria-label="t('participants.reset.action')"
        v-tooltip="t('participants.reset.action')"
        :loading="loading"
        :disabled="disabled"
        @click="askForConfirmation"
    />
</template>
